'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { useHeader } from '@/components/complex/header';
import { Settings } from 'lucide-react';
import { useBottomNavigation } from '@/components/complex/bottom-navigation';
import { useRouter } from 'next/navigation';
import Button from '@/components/primitives/button';
import { useAuth } from '@/context/auth-context';
import api from '@/api';
import { AccountDto } from '@/api/account';
import type { FeedDto } from '@/api/feed';
import SkeletonAccountProfile from '@/components/complex/skeleton-account-profile';
import Link from 'next/link';
import Caption from '@/components/complex/caption';
import Typography from '@/components/primitives/typography';
import styles from './styles.module.scss';
import Avatar from '@/components/primitives/avatar';
import { HDivider } from '@/components/primitives/divider';
import ErrorText from '@/components/common/error-text';
import FeedItems from '@/components/complex/feed-items';
import { useAccountFollow } from '@/libs/hooks/useAccountFollow';
import useMediaQuery from '@/libs/hooks/use-media-query';
import { PC_MEDIA_QUERY } from '@/libs/config/responsive';

interface AccountProfileScreenProps {
  account: AccountDto;
  initialFeeds?: FeedDto[];
}

interface StatItemProps {
  number: number;
  label: string;
}

const StatItem = (props: StatItemProps) => (
  <div className={styles.statItem}>
    <Typography className={styles.statNumber} size="h2">{props.number}</Typography>
    <Typography className={styles.statLabel} size="medium">{props.label}</Typography>
  </div>
);

// PC 프로필 요약의 팔로우 버튼(160×44 알약). 모바일 프로필은 기존처럼 팔로우를 두지 않는다.
const ProfileFollowButton = ({ account }: { account: AccountDto }) => {
  const { isFollowing, isLoading, handleFollow } = useAccountFollow(account, account.isFollowing);
  const [pressed, setPressed] = useState(isFollowing);

  // 요청이 끝나면 서버 결과로 맞춘다. 실패하면 isFollowing이 그대로라 누르기 전 상태로 돌아간다.
  useEffect(() => {
    if (!isLoading) setPressed(isFollowing);
  }, [isFollowing, isLoading]);

  return (
    <button
      type="button"
      className={styles.followButton}
      aria-pressed={pressed}
      aria-busy={isLoading}
      onClick={() => {
        if (isLoading) return;
        setPressed(!pressed);
        handleFollow(!pressed);
      }}
    >
      {pressed ? '팔로잉' : '팔로우'}
    </button>
  );
};

/**
 * Account profile screen component
 */
const AccountProfileScreen = ({ account, initialFeeds }: AccountProfileScreenProps) => {
  const router = useRouter();
  const auth = useAuth();
  const isOwnProfile = auth.me?.activeAccount?.name === account.name;

  const settingElement = useCallback(() => <Link href="/settings">
    <Settings width={24} height={24} strokeWidth={1.25} color="black" />
  </Link>, []);

  // Set up header with conditional settings button
  useHeader({
    useBack: true,
    desktopUseBack: false,
    title: account.name ? `${account.name} 님의 프로필` : '',
    desktopTitle: '프로필',
    rightElement: isOwnProfile ? settingElement : undefined,
  });

  const isPc = useMediaQuery(PC_MEDIA_QUERY);

  // Enable bottom navigation
  useBottomNavigation(true);


  return (
    <div className={styles.container}>

        <div className={styles.info}>
          {/* 모바일은 두 묶음을 display: contents로 풀어 기존 세로 순서를 그대로 둔다.
              PC 요약 패널은 [아바타 | 이름·핸들·소개]와 [통계 · 버튼] 두 묶음을 각각 세로 가운데에 맞춘다. */}
          <div className={styles.identity}>
            <div className={`${styles.profileHeader} ${styles.itemPadding}`}>
              <Avatar account={account} size={64} variant='w256'/>
              <div className={styles.profileInfo}>
                <h1 className={styles.profileName}>{account.nickname || account.name}</h1>
                <Typography className={styles.profileHandle}>@{account?.name}</Typography>
              </div>
            </div>

            {account?.caption ? (
              <div className={`${styles.bioText} ${styles.itemPadding}`}>
                <Caption text={account?.caption} />
              </div>
            ) : null}
          </div>

          <div className={styles.side}>
            {isOwnProfile && (
              <div className={`${styles.itemPadding} ${styles.actionButtons}`}>
                <Button
                  onClick={() => router.push('/settings/account/edit')}>
                  계정 수정
                </Button>
              </div>
            )}

            {isPc && auth.me?.activeAccount && !isOwnProfile && (
              <div className={styles.actionButtons}>
                <ProfileFollowButton account={account} />
              </div>
            )}

            <HDivider className={styles.divider} />

            <div className={`${styles.statsContainer} ${styles.itemPadding}`}>
              <StatItem number={account?.feedCount || 0} label="게시물" />
              <StatItem number={account?.followerCount || 0} label="팔로워" />
              {isOwnProfile ? (
                <Link href={`/accounts/${account.name}/following`}>
                  <StatItem number={account?.followingCount || 0} label="팔로잉" />
                </Link>
              ) : (
                <StatItem number={account?.followingCount || 0} label="팔로잉" />
              )}
            </div>

            <HDivider className={styles.divider} />
          </div>
        </div>

      {/* Feed List */}
      <div className={styles.feedSection}>
        <FeedItems
          accountName={account.name}
          feeds={initialFeeds}
          size={12}
          sortType={'LATEST'}
          onEmptyResult={() => (
            <div className={styles.emptyFeedContainer}>
              <Typography className={styles.emptyFeedText} align="center" size="small">
                아직 작성한 피드가 없어요
              </Typography>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default AccountProfileScreen;
